/* Razorpay checkout helper. Supports one-time orders + auto-renewing subscriptions. */
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;
const SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

let scriptPromise = null;

function loadRazorpay() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(window.Razorpay);
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.onload = () => resolve(window.Razorpay);
    s.onerror = () => { scriptPromise = null; reject(new Error('Failed to load Razorpay')); };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/** Trigger the checkout modal.
 *  @param {{type:'pro'|'program'|'pro-subscription', program_id?:string, onSuccess?:Function, onFailure?:Function}} opts
 */
export async function startCheckout(opts) {
  const { type, program_id, onSuccess, onFailure } = opts;
  if (!KEY_ID) {
    toast.error('Payments not configured');
    return;
  }
  try {
    await loadRazorpay();

    if (type === 'pro-subscription') {
      const subRes = await axios.post(
        `${API}/payments/create-subscription`, {}, { withCredentials: true }
      );
      const sub = subRes.data;
      const options = {
        key: sub.key_id,
        subscription_id: sub.subscription_id,
        name: 'fokyes.com',
        description: 'Pro — auto-renewing monthly',
        prefill: { name: sub.user.name || '', email: sub.user.email || '' },
        theme: { color: '#09090B' },
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API}/payments/verify-subscription`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature
            }, { withCredentials: true });
            if (verify.data.status === 'active') {
              toast.success('Pro plan activated — auto-renews monthly');
              onSuccess?.(verify.data);
            } else {
              toast.error('Verification failed');
              onFailure?.(verify.data);
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Subscription verification failed');
            onFailure?.(err);
          }
        },
        modal: {
          ondismiss: () => { toast.info('Subscription cancelled'); onFailure?.({ cancelled: true }); }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(resp.error?.description || 'Payment failed');
        onFailure?.(resp);
      });
      rzp.open();
      return;
    }

    // One-time order flow (program purchase, or legacy Pro one-time)
    const orderRes = await axios.post(
      `${API}/payments/create-order`,
      { type, program_id },
      { withCredentials: true }
    );
    const order = orderRes.data;

    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: 'fokyes.com',
      description: order.description,
      order_id: order.order_id,
      prefill: { name: order.user.name || '', email: order.user.email || '' },
      theme: { color: '#09090B' },
      handler: async (response) => {
        try {
          const verify = await axios.post(`${API}/payments/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          }, { withCredentials: true });
          if (verify.data.status === 'paid' || verify.data.status === 'already_paid') {
            toast.success('Payment successful');
            onSuccess?.(verify.data);
          } else {
            toast.error('Payment verification failed');
            onFailure?.(verify.data);
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Payment verification failed');
          onFailure?.(err);
        }
      },
      modal: {
        ondismiss: () => { toast.info('Payment cancelled'); onFailure?.({ cancelled: true }); }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      toast.error(resp.error?.description || 'Payment failed');
      onFailure?.(resp);
    });
    rzp.open();
  } catch (err) {
    const detail = err.response?.data?.detail || err.message || 'Checkout error';
    if (detail.includes('Pro plan not configured')) {
      toast.error('Pro subscription is being set up. For now, buy programs individually with the Razorpay test card.');
    } else {
      toast.error(detail);
    }
    onFailure?.(err);
  }
}
